import { Card, SpacedForwardSlash, OverlayedLoader } from '@mews-commander/ui';
import { Grid, Highlight, Stack, StackItem, Typography } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { LocalizedPercent, LocalizedText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { AvailabilityBlock, AvailabilityBlockState } from '@mews-commander/data-access';
import { WithAvailabilityBlockId } from '@mews-commander/types';
import { useCommanderSelector } from '@mews-commander/utils';
import { useAvailabilityBlockInfo } from '../../utils/useAvailabilityBlockInfo';
import {
    areAvailabilitiesFetching,
    getBlockAdjustmentAvailableNumber,
    getBlockAdjustmentReleaseNumber,
    getOccupancy,
    getSummaryBlockAdjustmentDataPickedUpValue,
} from '../../selectors';
import { NO_DECIMAL_PLACES } from '../../constants';
import ShowPickupButton from './buttons/ShowPickupButton';
import { StatusBar } from './StatusBar';
import { PickUpReservationButton } from './PickUpReservationButton';
import { ClientInfoSection } from './ClientInfo';
import { ChangeAvailabilityBlockStateButton } from './buttons/ChangeAvailabilityBlockStateButton';
import { DashboardCard } from './DashboardCard';
import { CancellationReasonCard } from './CancellationReasonCard';

const getUnconfirmedBlockCard = (availabilityBlock: AvailabilityBlock, available: number) => {
    switch (availabilityBlock.state) {
        case AvailabilityBlockState.Inquired:
            return (
                <Grid templateColumns="1fr">
                    <DashboardCard
                        variant="subtle"
                        textKey={TextKey.BlocksInquired}
                        content={<Highlight size="small">{available}</Highlight>}
                        button={<ChangeAvailabilityBlockStateButton availabilityBlock={availabilityBlock} />}
                        data-test-inquired={available}
                    />
                </Grid>
            );
        case AvailabilityBlockState.Optional:
            return (
                <Grid templateColumns="1fr">
                    <DashboardCard
                        variant="subtle"
                        textKey={TextKey.BlocksOptional}
                        content={<Highlight size="small">{available}</Highlight>}
                        button={<ChangeAvailabilityBlockStateButton availabilityBlock={availabilityBlock} />}
                        data-test-optional={available}
                    />
                </Grid>
            );
        case AvailabilityBlockState.Canceled:
            return <CancellationReasonCard availabilityBlock={availabilityBlock} available={available} />;
        case AvailabilityBlockState.Confirmed:
            return null;
    }
};

export const Header = ({ availabilityBlockId }: WithAvailabilityBlockId) => {
    const { availabilityBlock, isConfirmed } = useAvailabilityBlockInfo(availabilityBlockId);

    const available = useCommanderSelector((state) =>
        getBlockAdjustmentAvailableNumber(state, { availabilityBlockId })
    );
    const occupancy = useCommanderSelector((state) => getOccupancy(state, { availabilityBlockId }));

    const pickedUp = useCommanderSelector((state) =>
        getSummaryBlockAdjustmentDataPickedUpValue(state, { availabilityBlockId })
    );

    const released = useCommanderSelector((state) => getBlockAdjustmentReleaseNumber(state, { availabilityBlockId }));

    const isAvailabilitiesFetching = useCommanderSelector((state) => areAvailabilitiesFetching(state));

    if (!availabilityBlock) {
        return null;
    }

    const existsAnyAdjustments = available !== 0 || pickedUp !== 0 || released !== 0;

    const unconfirmedBlockCard = getUnconfirmedBlockCard(availabilityBlock, available);

    return (
        <Card relative data-test-header>
            <OverlayedLoader show={isAvailabilitiesFetching} />
            <Stack spacing="200" vertical>
                <StackItem>
                    <StatusBar availabilityBlockId={availabilityBlockId} />

                    {existsAnyAdjustments && isConfirmed && (
                        <Typography textStyle="titleSmall" variant="secondary" as="div">
                            <LocalizedText textKey={TextKey.Occupancy} />
                            {UnicodeEntity.NoBreakSpace}
                            <LocalizedPercent percent={occupancy} options={NO_DECIMAL_PLACES} />
                        </Typography>
                    )}
                </StackItem>

                {existsAnyAdjustments && unconfirmedBlockCard === null && (
                    <Grid
                        templateColumns="minmax(auto, 1fr) minmax(auto, 1fr) minmax(auto, 1fr)"
                        tabletOnly={{ templateColumns: 'minmax(auto, 1fr) minmax(auto, 1fr)' }}
                        largePhoneMax={{ templateColumns: '1fr' }}
                    >
                        <DashboardCard
                            variant="highlighted"
                            textKey={TextKey.PickedUp}
                            content={
                                <Stack itemAlignment="center">
                                    <Highlight size="small">{pickedUp}</Highlight>
                                    <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                                        {SpacedForwardSlash}
                                        {pickedUp + available}
                                    </Typography>
                                </Stack>
                            }
                            button={<ShowPickupButton availabilityBlock={availabilityBlock} />}
                            data-test-picked-up={pickedUp}
                        />

                        <DashboardCard
                            variant="subtle"
                            textKey={TextKey.Available}
                            content={<Highlight size="small">{available}</Highlight>}
                            button={<PickUpReservationButton availabilityBlockId={availabilityBlockId} />}
                            data-test-available={available}
                        />

                        <DashboardCard
                            variant="subtle"
                            textKey={TextKey.Released}
                            content={<Highlight size="small">{released}</Highlight>}
                            data-test-released={released}
                        />
                    </Grid>
                )}
                {unconfirmedBlockCard}

                <ClientInfoSection availabilityBlock={availabilityBlock} />
            </Stack>
        </Card>
    );
};
